import React, { useState, useEffect } from 'react';
import BottomNav from '../../components/BottomNav';
import ScoreCard from '../../components/ScoreCard';
import { useGame } from '../../context/GameContext';
import { MEMORY_PAIRS } from '../../data/gamesData';
import { shuffle, ShareBtn, ScoreImageBtn, RulesBtn , xpLabel, xpShareLine, xpForCard } from './shared';
import { fetchQuestions } from '../../utils/questionsAPI';
import LoadingScreen from '../../components/LoadingScreen';

// Une face de carte doit rester lisible d'un coup d'œil sur mobile : courte et
// non interrogative. Voir MEMORY_PAIRS pour le format de référence.
const MEMORY_FACE_MAX = 30;
const isUsableAsPair = (q) =>
  typeof q?.q === 'string' && typeof q?.a === 'string' &&
  q.q.trim().length > 0 && q.a.trim().length > 0 &&
  q.q.length <= MEMORY_FACE_MAX && q.a.length <= MEMORY_FACE_MAX &&
  !q.q.includes('?');

const makeCards = (apiPairs = []) => {
  const all    = shuffle([...apiPairs, ...MEMORY_PAIRS]);
  const picked = all.slice(0, 8);
  return shuffle(
    picked.flatMap((pair, i) => [
      { id: `a${i}`, pairId: i, text: pair.a, flipped: false, matched: false },
      { id: `b${i}`, pairId: i, text: pair.b, flipped: false, matched: false },
    ])
  );
};

// Wrapper : charge les questions API une fois, puis monte le board
const MemoryGame = ({ onBack, onEnd }) => {
  const [gameKey, setGameKey]   = useState(0);
  const [loading, setLoading]   = useState(true);
  const [apiPairs, setApiPairs] = useState([]);

  useEffect(() => {
    // fix : une question de quiz n'est pas une carte de Memory. Transformer
    // `question ↔ réponse` produisait des faces comme « Qui dirigea la
    // reconstruction des murailles de Jérusalem après l'exil ? » (signalé en
    // test fermé). Seules les paires courtes et non interrogatives sont
    // retenues ; en pratique le corpus actuel n'en fournit aucune, et le jeu
    // s'appuie alors sur MEMORY_PAIRS, conçu pour ce format.
    fetchQuestions({ n: 16 })
      .then(({ questions }) => setApiPairs(questions.filter(isUsableAsPair).map(q => ({ a: q.q, b: q.a }))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <MemoryBoard
      key={gameKey}
      onBack={onBack}
      onEnd={onEnd}
      apiPairs={apiPairs}
      onReplay={() => setGameKey(k => k + 1)}
    />
  );
};

const MemoryBoard = ({ onBack, onEnd, onReplay, apiPairs = [] }) => {
  const { profile } = useGame();
  const [cards, setCards]             = useState(() => makeCards(apiPairs));   // ← nouveau à chaque montage
  const [selected, setSelected]       = useState([]);
  const [moves, setMoves]             = useState(0);
  const [done, setDone]               = useState(false);
  const [shakePair, setShakePair]     = useState(null);
  const [wrongPair, setWrongPair]     = useState(null);       // correction immédiate
  const [copied, setCopied]           = useState(false);
  const [showScoreCard, setShowScoreCard] = useState(false);
  const xpFiredRef = React.useRef(false);
  // fix V1 : XP confirmee par le serveur — undefined = en attente, null = echec.
  const [serverXp, setServerXp] = useState(undefined);

  React.useEffect(() => {
    if (cards.length > 0 && cards.every(c => c.matched)) setDone(true);
  }, [cards]);

  const flip = (id) => {
    if (selected.length === 2) return;
    const card = cards.find(c => c.id === id);
    if (!card || card.flipped || card.matched) return;
    const nc = cards.map(c => c.id === id ? { ...c, flipped: true } : c);
    setCards(nc);
    const ns = [...selected, id];
    setSelected(ns);
    if (ns.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = ns.map(sid => nc.find(c => c.id === sid));
      if (a.pairId === b.pairId) {
        setTimeout(() => {
          setCards(c => c.map(x => ns.includes(x.id) ? { ...x, matched: true } : x));
          setSelected([]);
          setWrongPair(null);
        }, 500);
      } else {
        // Mauvaise paire → afficher les deux textes brièvement
        setWrongPair({ a: a.text, b: b.text });
        setShakePair(ns);
        setTimeout(() => {
          setCards(c => c.map(x => ns.includes(x.id) ? { ...x, flipped: false } : x));
          setSelected([]);
          setShakePair(null);
          setWrongPair(null);
        }, 1100);
      }
    }
  };

  const perfect = moves <= 8;

  if (done && !xpFiredRef.current) {
    xpFiredRef.current = true;
    const pairs = cards.length / 2;
    Promise.resolve(onEnd?.({ correct: pairs, total: pairs, hintsUsed: 0 }))
      .then(r => setServerXp(r && typeof r.xpGained === 'number' ? r.xpGained : null))
      .catch(() => setServerXp(null));
  }

  const copyScore = () => {
    const text = `🃏 Bible Memory — ${profile.name || 'Joueur'}\n✅ ${cards.filter(c => c.matched).length / 2}/8 paires\n🎯 ${moves} coups${perfect ? ' 🏆 Parfait !' : ''}${xpShareLine(serverXp)}\n🎮 Manna`;
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (done) return (
    <div className="page-content">
      {showScoreCard && (
        <ScoreCard
          gameName="Bible Memory" playerName={profile.name || 'Joueur'} playerAvatar={profile.avatar}
          score={`${moves} coups`} xp={xpForCard(serverXp)}
          details={[{ label: 'Paires trouvées', value: '8/8' }, { label: 'Performance', value: perfect ? '🏆 Parfait' : `${moves} coups` }]}
          onClose={() => setShowScoreCard(false)}
        />
      )}
      <div style={{ textAlign:'center', marginBottom:'1.25rem' }}>
        <div style={{ fontSize:'3rem' }}>🎉</div>
        <h2 style={{ margin:'.5rem 0' }}>{perfect ? 'Parfait !' : 'Terminé !'}</h2>
        <p className="text-small">Toutes les paires trouvées</p>
      </div>
      <div className="card-white" style={{ marginBottom:'1rem' }}>
        {[['Paires trouvées','8/8'],['Coups',moves],['XP gagnés', xpLabel(serverXp)]].map(([l,v]) => (
          <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'.55rem 0', borderBottom:'1px solid var(--gray-200)' }}>
            <span style={{ color:'var(--ink-light)' }}>{l}</span>
            <span style={{ fontFamily:'var(--font-display)', fontWeight:700, color:'var(--ink)' }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap' }}>
        <button className="btn btn-primary" onClick={onReplay}>🔄 Rejouer</button>
        <ShareBtn onClick={copyScore} copied={copied} />
        <ScoreImageBtn onClick={() => setShowScoreCard(true)} />
        <button className="btn btn-secondary" onClick={onBack}>← Menu</button>
      </div>
      <BottomNav />
    </div>
  );

  return (
    <div className="page-content">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.75rem' }}>
        <button className="btn btn-ghost" onClick={onBack}>← Retour</button>
        <span style={{ fontFamily:'var(--font-display)', color:'var(--gold-light)', fontSize:'.9rem' }}>BIBLE MEMORY</span>
        <div style={{ display:'flex', gap:'.4rem', alignItems:'center' }}>
          <span style={{ fontFamily:'var(--font-display)', color:'var(--gold-light)', fontSize:'.9rem' }}>{moves} coups</span>
          <RulesBtn gameId="memory" />
        </div>
      </div>

      {/* Feedback mauvaise paire */}
      {wrongPair && (
        <div style={{ background:'rgba(230,57,70,.12)', border:'1.5px solid var(--crimson)', borderRadius:10, padding:'.6rem 1rem', marginBottom:'.75rem', fontSize:'.83rem', animation:'fadeUp .15s ease both' }}>
          <span style={{ color:'var(--crimson)', fontWeight:700 }}>❌ Pas une paire : </span>
          <span style={{ color:'var(--gray-400)' }}>{wrongPair.a}</span>
          <span style={{ color:'var(--gray-400)' }}> ≠ </span>
          <span style={{ color:'var(--gray-400)' }}>{wrongPair.b}</span>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'.4rem', marginBottom:'1rem' }}>
        {cards.map((card, i) => (
          // fix a11y : vrai <button> — tabulation, Entrée et Espace natifs.
          // Le libellé ne révèle jamais le texte d'une carte face cachée.
          <button key={card.id}
            type="button"
            className="memory-card"
            onClick={() => flip(card.id)}
            disabled={card.matched}
            aria-label={
              card.matched  ? `Paire trouvée : ${card.text}`
              : card.flipped ? `Carte révélée : ${card.text}`
              : `Carte face cachée, position ${i + 1} sur ${cards.length}`
            }
            style={{
              height: 72, borderRadius: 10, cursor: card.matched ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', padding: '.3rem',
              fontSize: '.65rem', lineHeight: 1.3, fontWeight: 600,
              transition: 'all .25s',
              // Neutralise les styles par défaut du navigateur, rendu identique.
              width: '100%', margin: 0, appearance: 'none',
              fontFamily: 'inherit', opacity: 1,
              background: card.matched
                ? 'rgba(45,107,79,.25)'
                : card.flipped
                  ? 'var(--parch)'
                  : shakePair?.includes(card.id)
                    ? 'rgba(230,57,70,.2)'
                    : 'rgba(201,168,76,.1)',
              border: card.matched
                ? '1.5px solid rgba(45,107,79,.4)'
                : card.flipped
                  ? '1.5px solid rgba(201,168,76,.4)'
                  : '1.5px solid rgba(201,168,76,.15)',
              color: card.matched ? 'var(--sage-light)' : card.flipped ? 'var(--ink)' : 'transparent',
              transform: shakePair?.includes(card.id) ? 'scale(.95)' : 'scale(1)',
            }}
          >
            <span aria-hidden="true">{card.matched || card.flipped ? card.text : '?'}</span>
          </button>
        ))}
      </div>
      <BottomNav />
    </div>
  );
};

export default MemoryGame;
